import { useEffect, useState } from 'react';
import { callable } from '../lib/callable';

interface AlertCard {
  announcementId: string;
  slug: string;
  title: string;
  publishedAt: string | null;
}

const DISMISS_KEY = 'banner_dismissed_at';
const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;

function dismissedThisSession(): boolean {
  try { return !!sessionStorage.getItem(DISMISS_KEY); } catch { return false; }
}

// Thin strip above the founders banner showing the 1-2 most recent published
// announcements (last 14 days). Uses the listAnnouncements callable (Phase 1 rules
// deny direct client reads of the collection), refetching on mount + tab refocus.
// Rotates between two alerts every 3s; dismissable per session.
export function LatestAlertsBanner() {
  const [alerts, setAlerts] = useState<AlertCard[]>([]);
  const [idx, setIdx] = useState(0);
  const [dismissed, setDismissed] = useState(dismissedThisSession());

  useEffect(() => {
    if (dismissed) return;
    let cancelled = false;
    const load = () => {
      callable<{ limit: number }, { announcements: AlertCard[] }>('listAnnouncements', { limit: 5 })
        .then((r) => {
          if (cancelled) return;
          const now = Date.now();
          const recent = r.data.announcements.filter((a) => {
            if (!a.publishedAt) return false;
            const t = new Date(a.publishedAt).getTime();
            return Number.isFinite(t) && now - t <= FOURTEEN_DAYS_MS;
          }).slice(0, 2);
          setAlerts(recent);
        })
        .catch(() => { if (!cancelled) setAlerts([]); });
    };
    load();
    const onFocus = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onFocus);
    return () => { cancelled = true; document.removeEventListener('visibilitychange', onFocus); };
  }, [dismissed]);

  // Rotate between the two alerts.
  useEffect(() => {
    if (alerts.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % alerts.length), 3000);
    return () => clearInterval(t);
  }, [alerts.length]);

  if (dismissed || alerts.length === 0) return null;
  const current = alerts[Math.min(idx, alerts.length - 1)]!;

  function dismiss() {
    try { sessionStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* sessionStorage unavailable */ }
    setDismissed(true);
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minHeight: 50, padding: '0.5rem 0.85rem', marginBottom: '1rem', background: 'linear-gradient(90deg, #fff1f2 0%, #fef2f2 100%)', border: '1px solid #fecaca', borderRadius: 10 }}>
      <style>{`@keyframes laPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.45; } }`}</style>
      <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.2rem 0.55rem', background: '#dc2626', color: 'white', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.03em' }}>
        <span aria-hidden="true" style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: 'white', animation: 'laPulse 1.4s ease-in-out infinite' }} />
        🔔 LATEST
      </span>
      <a href={`/updates/${current.slug}`} style={{ flex: 1, minWidth: 0, color: '#1a4480', fontWeight: 600, fontSize: '0.92rem', textDecoration: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {current.title} →
      </a>
      <a href="/updates" style={{ flexShrink: 0, color: '#6b7280', fontSize: '0.82rem', textDecoration: 'none', fontWeight: 500 }} className="la-viewall">View all updates →</a>
      <button onClick={dismiss} aria-label="Dismiss" style={{ flexShrink: 0, background: 'none', border: 'none', color: '#6b7280', fontSize: '1.2rem', lineHeight: 1, cursor: 'pointer', padding: '0 0.25rem' }}>×</button>
      <style>{`@media (max-width: 600px) { .la-viewall { display: none; } }`}</style>
    </div>
  );
}
